/**
 * GroupChatPage — 群聊演示页面
 *
 * 模块架构：
 * ┌──────────────────────────────────────────────────────┐
 * │  GroupChatPage（编排层）                               │
 * │  ├─ hooks/                                            │
 * │  │   └─ useGroupChat     — 状态管理（用户/消息/连接）   │
 * │  ├─ components/                                       │
 * │  │   ├─ ChatMessageList  — 消息列表                   │
 * │  │   └─ UserModal        — 用户信息弹窗               │
 * │  ├─ utils/                                            │
 * │  │   ├─ mockData         — 模拟消息服务适配器          │
 * │  │   └─ storage          — localStorage 工具          │
 * │  └─ types                — 模块类型定义                │
 * │                                                       │
 * └── 外部依赖: GroupChatInput (src/components/ad-ui/)     │
 * └──────────────────────────────────────────────────────┘
 *
 * 数据流：
 *   用户输入 → GroupChatInput → useGroupChat.sendMessage()
 *       │                            └─ 加入本地消息（sending）
 *       │                            └─ 委托 adapter.send()
 *       │                                   └─ mock 模拟延迟后回调
 *       │                                          └─ 更新消息状态
 *       │
 *   消息列表 ← useGroupChat.messages ← ChatMessageList 渲染
 */

import React from 'react';
import GroupChatInput from '@/components/GroupChatInput';
import ChatMessageList from './components/ChatMessageList';
import UserModal from './components/UserModal';
import { useGroupChat } from './hooks/useGroupChat';
import './index.scss';

/**
 * 群聊演示页面主组件
 */
const GroupChatPage: React.FC = () => {
  const {
    currentUser,
    showUserModal,
    messages,
    connectionStatus,
    sending,
    setUser,
    sendMessage,
    retryMessage,
  } = useGroupChat();

  return (
    <div className="group-chat-page">
      {/* ===== 用户信息设置弹窗 ===== */}
      {showUserModal && <UserModal onConfirm={setUser} />}

      {/* ===== 页面内容 ===== */}
      <div className="group-chat-container">
        {/* ---- 顶部栏 ---- */}
        <div className="group-chat-header">
          <div className="group-chat-header-info">
            <h2 className="group-chat-header-title">群聊演示</h2>
            <span className={`group-chat-header-status group-chat-header-status-${connectionStatus}`}>
              {connectionStatus === 'connected' && '已连接'}
              {connectionStatus === 'connecting' && '连接中...'}
              {connectionStatus === 'disconnected' && '未连接'}
            </span>
          </div>
          {/* 当前用户信息（已登录时显示） */}
          {currentUser && (
            <div className="group-chat-header-user">
              <span className="group-chat-header-username">{currentUser.nickname}</span>
              {currentUser.avatar ? (
                <img
                  src={currentUser.avatar}
                  alt={currentUser.nickname}
                  className="group-chat-header-avatar"
                />
              ) : (
                <span className="group-chat-header-avatar group-chat-header-avatar-text">
                  {currentUser.nickname.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          )}
        </div>

        {/* ---- 消息列表 ---- */}
        {currentUser && (
          <ChatMessageList
            messages={messages}
            currentUserId={currentUser.userId}
            onRetry={retryMessage}
          />
        )}

        {/* ---- 输入区域 ---- */}
        {currentUser && (
          <div className="group-chat-input-area">
            <GroupChatInput
              onSend={(msg) =>
                sendMessage({ segments: msg.segments })
              }
              sending={sending}
              placeholder="输入消息，Enter 发送"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupChatPage;
